import { useEffect } from 'react'

import { useLocation, useNavigate, useRouteError } from 'react-router'

import { ErrorView } from './ErrorView.tsx'

import './shell.css'

/**
 * 라우트 단 에러 경계 — **렌더 중 예외**가 났을 때 무엇이 뜨는가.
 *
 * 두 개를 내보낸다. 갈리는 것은 **셸이 살아 있느냐** 하나다.
 *
 * | 내보내는 것 | 어디에 다나 | 무엇이 남나 |
 * | --- | --- | --- |
 * | `RouteErrorScreen` | 셸 «자식»의 이름 없는 레이아웃 라우트 | 헤더 · 작업 줄 · **E-STOP** 이 산다 |
 * | `AppErrorScreen`   | **라우트 표의 최상위 항목마다** | 셸을 못 그린다. 면 하나 위 최소 화면 |
 *
 * 🔴 **최상위 항목마다 다는 것이 필수다.** react-router 는 `errorElement` 가 없어도
 *    **뿌리 매치(`index === 0`)에 자기 기본 경계를 꽂는다**(`react-router/dist/.../lib/hooks.js`
 *    의 `match.route.ErrorBoundary || match.route.errorElement || index === 0`).
 *    비워 두면 그 기본 화면이 뜬다 — 2026-09-22 실물로 확인했다:
 *    **「Unexpected Application Error!」 + 영문 스택 전문**이 화면에 그대로 깔린다.
 *    백지는 아니지만 우리 규약(한글 문구 · 원문 비노출)을 정면으로 어긴다.
 * 🔴 **데이터 오류(4xx/5xx)는 여기 오지 않는다.** 그건 각 컨테이너가 `StateNotice` 로
 *    화면 안에 그린다 — 조회가 막힌 것과 화면이 깨진 것은 사람이 할 일이 다르다.
 *    라우트 로더/액션도 안 쓴다(자료는 전부 react-query). **렌더 예외 전용이다.**
 *
 * 「다시 시도」는 경계를 풀고 **같은 자리를 다시 그린다**. react-router 의 렌더 오류 상태는
 * **`location` 객체가 바뀔 때** 풀리므로(`RenderErrorBoundary.getDerivedStateFromProps`)
 * 같은 주소로 `replace` 이동을 한 번 한다 — 주소는 그대로고 이력도 안 쌓인다.
 * ⚠ `useRevalidator().revalidate()` 로는 **안 풀린다**(2026-09-22 실측). 우리는 라우트 로더가
 *   0건이라 재검증이 `idle` 을 떠나지 않아 그 가지가 안 탄다 — 되돌리지 마라.
 */
export function RouteErrorScreen() {
  return <RouteError />
}

/** 셸까지 못 그린 경우. `RouteErrorScreen` 과 **같은 경계**이고 얼굴만 다르다 */
export function AppErrorScreen() {
  return <RouteError fatal />
}

function RouteError({ fatal }: { fatal?: boolean }) {
  const error = useRouteError()
  const navigate = useNavigate()
  const location = useLocation()

  // 화면에 원문을 안 쓰는 대신 여기 남긴다 — 개발·현장 로그 수집 양쪽에서 이게 유일한 흔적이다
  useEffect(() => {
    console.error('[화면 렌더 실패]', error)
  }, [error])

  const view = (
    <ErrorView
      error={error}
      {...(fatal === true ? { fatal: true } : {})}
      onHome={() => void navigate('/')}
      onRetry={() =>
        void navigate(
          { pathname: location.pathname, search: location.search, hash: location.hash },
          { replace: true },
        )
      }
    />
  )

  /*
   * 셸이 죽은 자리에는 셸이 없다 — 이 `errorElement` 가 `ConsoleLayout` «을» 대신한다.
   * 로그인 화면과 같은 빈 셸(`shell--plain`)을 직접 세워야 본문이 화면 한가운데에 선다.
   */
  return fatal === true ? (
    <div className="shell shell--plain">
      <div className="shell__body">{view}</div>
    </div>
  ) : (
    view
  )
}
